import React, {
  ComponentType,
  createContext,
  PropsWithChildren,
  RefAttributes,
  useContext,
} from 'react';
import { LinkProps as RouterLinkProps } from 'react-router-dom';
import { BucketLogData } from '@bucketplace/bucket-log';

import { LogDataFormGroupProps, UnitNode } from '../../components';
import { LinkProps, OpenOption } from '../../editorComponents/Link/Link';

type LinkElementProps = RouterLinkProps & RefAttributes<HTMLAnchorElement> & {
  openOption: OpenOption,
  logData?: BucketLogData,
};

export interface LinkContextValue {
  element?: ComponentType<LinkElementProps>;
  setLogData?: (unitNode: UnitNode<Partial<Pick<LinkProps, 'logData' | 'to'>>>) => LogDataFormGroupProps;
  callbacks?: {
    onClick?: (targetId: string) => void,
  };
}

function scrollToTarget(targetId: string): void {
  document.querySelector(`#${targetId}`)?.scrollIntoView({
    block: 'center',
    behavior: 'smooth',
  });
}

export const initialLinkContextValue: Required<Omit<LinkContextValue, 'setLogData'>> = {
  element: ({ to, ...props }) => (
    <a href={typeof to === 'string' ? to : undefined} {...props} />
  ),
  callbacks: {
    onClick: scrollToTarget,
  },
};

const LinkContext = createContext<LinkContextValue>(initialLinkContextValue);
export const useLinkContext = () => useContext(LinkContext);

export function LinkContextProvider({
  children,
  value = initialLinkContextValue,
}: PropsWithChildren<{ value?: LinkContextValue }>) {
  return <LinkContext.Provider value={value}>{ children }</LinkContext.Provider>;
}
